import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, Loader2, ShieldAlert } from 'lucide-react';

// CTH-TC-077 — consultant-only traceability of original assessment responses.
export const ResponsesAuditDialog = ({ assignmentId, open, onOpenChange }) => {
  const { i18n } = useTranslation();
  const es = (i18n.language || 'es').startsWith('es');
  const L = (a, b) => (es ? a : b);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !assignmentId) return;
    setData(null);
    api.get(`/admin/assignments/${assignmentId}/responses`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [open, assignmentId]);

  const exportCsv = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/assignments/${assignmentId}/responses/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `respuestas-${assignmentId}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="responses-audit-dialog">
        <DialogHeader>
          <DialogTitle>{L('Respuestas originales de la evaluación', 'Original assessment responses')}</DialogTitle>
        </DialogHeader>
        <div className="flex items-start gap-2 text-xs rounded-md border border-amber-200 bg-amber-50 p-2.5 text-amber-900">
          <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
          {L('Vista confidencial exclusiva para consultores CTH. Incluye la clave de puntuación donde la metodología lo permite. No compartir con el cliente ni con el candidato. Las respuestas y puntajes originales no se modifican.',
             'Confidential view for CTH consultants only. Includes the scoring key where the methodology permits. Do not share with the client or the candidate. Original responses and scores are never altered.')}
        </div>
        {!data ? (
          <div className="py-10 flex justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <>
            <div className="flex items-center justify-between flex-wrap gap-2 text-xs text-muted-foreground">
              <span data-testid="responses-meta">
                {data.meta.answered}/{data.meta.total_questions} {L('respondidas', 'answered')}
                {data.meta.submitted_at ? ` · ${L('Enviado', 'Submitted')}: ${String(data.meta.submitted_at).slice(0, 16).replace('T', ' ')}` : ''}
                {data.meta.scoring_model ? ` · ${L('Modelo', 'Model')}: ${data.meta.scoring_model}` : ''}
                {data.meta.restricted_items > 0 && ` · ${data.meta.restricted_items} ${L('ítems con clave restringida', 'items with restricted key')}`}
              </span>
              <Button size="sm" variant="outline" onClick={exportCsv} disabled={busy} className="gap-1.5" data-testid="responses-export-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} CSV
              </Button>
            </div>
            <div className="space-y-3" data-testid="responses-list">
              {data.items.map((q) => (
                <div key={q.question_id} className="rounded-md border p-3 text-sm space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium">{q.order}. {q.prompt}</p>
                    {q.item_score != null && (
                      <Badge variant="outline" className="shrink-0 tabular-nums text-xs">
                        {q.item_score}{q.item_max != null ? `/${q.item_max}` : ''}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {q.section && <span>{q.section}</span>}
                    {q.section && (q.competencies || []).length > 0 && ' · '}
                    {(q.competencies || []).join(', ')}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {(q.options || []).map((o, i) => {
                      const selected = (q.response || []).includes(o.label);
                      return (
                        <span key={i}
                          className={`text-[11px] px-2 py-0.5 rounded-full border ${selected
                            ? 'bg-teal-600 text-white border-teal-600'
                            : o.is_key ? 'border-emerald-400 text-emerald-700 bg-emerald-50'
                            : 'border-border text-muted-foreground'}`}>
                          {o.label}{o.is_key ? ` ✓${L(' clave', ' key')}` : ''}
                        </span>
                      );
                    })}
                    {(q.options || []).length === 0 && q.response_text && (
                      <span className="text-xs italic">{q.response_text}</span>
                    )}
                  </div>
                  {!q.key_available && (
                    <p className="text-[11px] text-amber-700">
                      {L('Clave de puntuación no divulgable para este ítem (restricción de licencia del instrumento).',
                         'Scoring key not disclosable for this item (instrument licensing restriction).')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
